import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

function GeburtstagManagerV2() {
    const { user } = useAuth();
    const [themes, setThemes] = useState([]);
    const [packages, setPackages] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [blockedDates, setBlockedDates] = useState([]);
    const [activeTab, setActiveTab] = useState('bookings');

    const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user?.token}`
    };

    // Theme form
    const [showThemeForm, setShowThemeForm] = useState(false);
    const [editingTheme, setEditingTheme] = useState(null);
    const [themeData, setThemeData] = useState({
        name: '', description: '', image: '', min_age: 6, max_age: 14, sort_order: 0
    });

    // Package form
    const [showPackageForm, setShowPackageForm] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);
    const [packageData, setPackageData] = useState({
        name: '', description: '', price: 199, price_per_child: 0, includes: '', recommended: false, sort_order: 0
    });

    // Block date form
    const [showBlockForm, setShowBlockForm] = useState(false);
    const [blockData, setBlockData] = useState({ date: '', reason: '' });

    // Invoice modal
    const [invoiceBooking, setInvoiceBooking] = useState(null);
    const [invoiceNumChildren, setInvoiceNumChildren] = useState('');
    const [invoiceApplyVat, setInvoiceApplyVat] = useState(false);
    const [invoiceVatRate, setInvoiceVatRate] = useState(19);
    const [invoiceLoading, setInvoiceLoading] = useState(false);

    useEffect(() => {
        fetchAll();
    }, []);

    const fetchAll = () => {
        fetchThemes();
        fetchPackages();
        fetchBookings();
        fetchBlockedDates();
    };

    const fetchThemes = () => {
        fetch('/api/v2/geburtstag/admin/themes', { headers: authHeaders })
            .then(res => res.json())
            .then(data => setThemes(data.data || []))
            .catch(err => console.error('Fehler:', err));
    };

    const fetchPackages = () => {
        fetch('/api/v2/geburtstag/admin/packages', { headers: authHeaders })
            .then(res => res.json())
            .then(data => setPackages(data.data || []))
            .catch(err => console.error('Fehler:', err));
    };

    const fetchBookings = () => {
        fetch('/api/v2/geburtstag/bookings', { headers: authHeaders })
            .then(res => res.json())
            .then(data => setBookings(data.data || []))
            .catch(err => console.error('Fehler:', err));
    };

    const fetchBlockedDates = () => {
        fetch('/api/v2/geburtstag/blocked-dates', { headers: authHeaders })
            .then(res => res.json())
            .then(data => setBlockedDates(data.data || []))
            .catch(err => console.error('Fehler:', err));
    };

    // Theme handlers
    const handleThemeSubmit = (e) => {
        e.preventDefault();
        const url = editingTheme ? `/api/v2/geburtstag/themes/${editingTheme.id}` : '/api/v2/geburtstag/themes';
        const method = editingTheme ? 'PUT' : 'POST';

        fetch(url, { method, headers: authHeaders, body: JSON.stringify(themeData) })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    fetchThemes();
                    resetThemeForm();
                } else {
                    alert('Fehler: ' + (data.error || 'Unbekannt'));
                }
            })
            .catch(err => alert('Fehler: ' + err.message));
    };

    const handleEditTheme = (theme) => {
        setEditingTheme(theme);
        setThemeData({
            name: theme.name, description: theme.description || '',
            image: theme.image || '', min_age: theme.min_age || 6,
            max_age: theme.max_age || 14, sort_order: theme.sort_order || 0
        });
        setShowThemeForm(true);
    };

    const handleDeleteTheme = (id) => {
        if (!window.confirm('Thema wirklich loeschen?')) return;
        fetch(`/api/v2/geburtstag/themes/${id}`, { method: 'DELETE', headers: authHeaders })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') fetchThemes();
                else alert('Fehler: ' + (data.error || 'Unbekannt'));
            });
    };

    const toggleThemeActive = (theme) => {
        fetch(`/api/v2/geburtstag/themes/${theme.id}`, {
            method: 'PUT', headers: authHeaders,
            body: JSON.stringify({ active: !theme.active })
        })
            .then(res => res.json())
            .then(data => { if (data.message === 'success') fetchThemes(); });
    };

    const resetThemeForm = () => {
        setShowThemeForm(false);
        setEditingTheme(null);
        setThemeData({ name: '', description: '', image: '', min_age: 6, max_age: 14, sort_order: 0 });
    };

    // Package handlers
    const handlePackageSubmit = (e) => {
        e.preventDefault();
        const url = editingPackage ? `/api/v2/geburtstag/packages/${editingPackage.id}` : '/api/v2/geburtstag/packages';
        const method = editingPackage ? 'PUT' : 'POST';

        fetch(url, { method, headers: authHeaders, body: JSON.stringify(packageData) })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    fetchPackages();
                    resetPackageForm();
                } else {
                    alert('Fehler: ' + (data.error || 'Unbekannt'));
                }
            })
            .catch(err => alert('Fehler: ' + err.message));
    };

    const handleEditPackage = (pkg) => {
        setEditingPackage(pkg);
        setPackageData({
            name: pkg.name, description: pkg.description || '',
            price: pkg.price, price_per_child: pkg.price_per_child || 0,
            includes: pkg.includes || '',
            recommended: pkg.recommended || false, sort_order: pkg.sort_order || 0
        });
        setShowPackageForm(true);
    };

    const handleDeletePackage = (id) => {
        if (!window.confirm('Paket wirklich loeschen?')) return;
        fetch(`/api/v2/geburtstag/packages/${id}`, { method: 'DELETE', headers: authHeaders })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') fetchPackages();
                else alert('Fehler: ' + (data.error || 'Unbekannt'));
            });
    };

    const resetPackageForm = () => {
        setShowPackageForm(false);
        setEditingPackage(null);
        setPackageData({ name: '', description: '', price: 199, price_per_child: 0, includes: '', recommended: false, sort_order: 0 });
    };

    // Booking handlers
    const handleCancelBooking = (id) => {
        if (!window.confirm('Buchung wirklich stornieren?')) return;
        fetch(`/api/v2/geburtstag/bookings/${id}`, {
            method: 'PUT', headers: authHeaders, body: JSON.stringify({ status: 'cancelled' })
        })
            .then(res => res.json())
            .then(data => { if (data.message === 'success') fetchBookings(); });
    };

    const handleDeleteBooking = (id) => {
        if (!window.confirm('Buchung wirklich loeschen?')) return;
        fetch(`/api/v2/geburtstag/bookings/${id}`, { method: 'DELETE', headers: authHeaders })
            .then(res => res.json())
            .then(data => { if (data.message === 'success') fetchBookings(); });
    };

    // Invoice handlers
    const openInvoiceModal = (booking) => {
        setInvoiceBooking(booking);
        setInvoiceNumChildren(booking.num_children);
        setInvoiceApplyVat(false);
        setInvoiceVatRate(19);
    };

    const closeInvoiceModal = () => {
        if (invoiceLoading) return;
        setInvoiceBooking(null);
    };

    const handleCreateInvoice = () => {
        if (!invoiceBooking) return;
        setInvoiceLoading(true);
        fetch(`/api/invoices/geburtstag/${invoiceBooking.id}`, {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({
                num_children: invoiceNumChildren,
                apply_vat: invoiceApplyVat,
                vat_rate: invoiceVatRate
            })
        })
            .then(res => res.json())
            .then(data => {
                setInvoiceLoading(false);
                if (data.message === 'success') {
                    alert(`Rechnung ${data.data.invoice_number} erstellt!`);
                    setInvoiceBooking(null);
                } else {
                    alert('Fehler: ' + (data.error || 'Unbekannt'));
                }
            })
            .catch(() => {
                setInvoiceLoading(false);
                alert('Netzwerkfehler beim Erstellen der Rechnung');
            });
    };

    // Block date handlers
    const handleBlockSubmit = (e) => {
        e.preventDefault();
        fetch('/api/v2/geburtstag/blocked-dates', {
            method: 'POST', headers: authHeaders, body: JSON.stringify(blockData)
        })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    fetchBlockedDates();
                    setShowBlockForm(false);
                    setBlockData({ date: '', reason: '' });
                }
            });
    };

    const handleUnblockDate = (date) => {
        fetch(`/api/v2/geburtstag/blocked-dates/${date}`, { method: 'DELETE', headers: authHeaders })
            .then(res => res.json())
            .then(data => { if (data.message === 'success') fetchBlockedDates(); });
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatPrice = (price) => parseFloat(price).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

    const upcomingBookings = bookings.filter(b => b.status !== 'cancelled' && new Date(b.date) >= new Date());
    const pastBookings = bookings.filter(b => b.status === 'cancelled' || new Date(b.date) < new Date());

    return (
        <div className="container-fluid">
            <h4>Geburtstage verwalten (Neues System)</h4>
            <p className="text-muted">
                Verfuegbare Zeiten: Freitag 17-20 Uhr, Samstag 14-20 Uhr (3h Slots)
            </p>

            <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'bookings' ? 'active' : ''}`} onClick={() => setActiveTab('bookings')}>
                        Buchungen ({upcomingBookings.length})
                    </button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'themes' ? 'active' : ''}`} onClick={() => setActiveTab('themes')}>
                        Themen ({themes.length})
                    </button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'packages' ? 'active' : ''}`} onClick={() => setActiveTab('packages')}>
                        Pakete ({packages.length})
                    </button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'blocked' ? 'active' : ''}`} onClick={() => setActiveTab('blocked')}>
                        Gesperrt ({blockedDates.length})
                    </button>
                </li>
            </ul>

            {/* BOOKINGS TAB */}
            {activeTab === 'bookings' && (
                <>
                    <h5>Kommende Buchungen</h5>
                    {upcomingBookings.length === 0 ? (
                        <div className="alert alert-info">Keine kommenden Buchungen.</div>
                    ) : (
                        <div className="table-responsive mb-4">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Datum</th>
                                        <th>Uhrzeit</th>
                                        <th>Thema</th>
                                        <th>Paket</th>
                                        <th>Kind</th>
                                        <th>Eltern</th>
                                        <th>Kinder</th>
                                        <th>Preis</th>
                                        <th>Status</th>
                                        <th>Aktionen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {upcomingBookings.map(b => (
                                        <tr key={b.id}>
                                            <td>{formatDate(b.date)}</td>
                                            <td>{b.start_time?.substring(0, 5)} Uhr</td>
                                            <td>
                                                {b.theme_name || b.custom_theme || <span className="text-muted">Individuell</span>}
                                            </td>
                                            <td><span className="badge bg-primary">{b.package_name}</span></td>
                                            <td>
                                                <strong>{b.child_name}</strong>
                                                {b.child_age && <><br /><small>wird {b.child_age}</small></>}
                                            </td>
                                            <td>
                                                {b.parent_name}<br />
                                                <a href={`mailto:${b.parent_email}`}><small>{b.parent_email}</small></a>
                                                {b.parent_phone && <><br /><small>{b.parent_phone}</small></>}
                                            </td>
                                            <td><span className="badge bg-secondary">{b.num_children}</span></td>
                                            <td><strong>{formatPrice(b.total_price)}</strong></td>
                                            <td><span className={`badge ${b.status === 'confirmed' ? 'bg-success' : 'bg-warning'}`}>{b.status}</span></td>
                                            <td>
                                                <button className="btn btn-sm btn-outline-success me-1" onClick={() => openInvoiceModal(b)}>
                                                    Rechnung
                                                </button>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleCancelBooking(b.id)}>
                                                    Stornieren
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {pastBookings.length > 0 && (
                        <>
                            <h5>Vergangene/Stornierte</h5>
                            <div className="table-responsive">
                                <table className="table table-sm text-muted">
                                    <tbody>
                                        {pastBookings.slice(0, 10).map(b => (
                                            <tr key={b.id}>
                                                <td>{formatDate(b.date)}</td>
                                                <td>{b.child_name}</td>
                                                <td>{b.theme_name || b.custom_theme}</td>
                                                <td><span className={`badge ${b.status === 'cancelled' ? 'bg-danger' : 'bg-secondary'}`}>{b.status}</span></td>
                                                <td>
                                                    {b.status !== 'cancelled' && (
                                                        <button className="btn btn-sm btn-outline-success me-1" onClick={() => openInvoiceModal(b)}>
                                                            Rechnung
                                                        </button>
                                                    )}
                                                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteBooking(b.id)}>
                                                        Loeschen
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </>
            )}

            {/* THEMES TAB */}
            {activeTab === 'themes' && (
                <>
                    <button className="btn btn-primary mb-3" onClick={() => setShowThemeForm(true)}>
                        + Neues Thema
                    </button>

                    {showThemeForm && (
                        <div className="card mb-4">
                            <div className="card-body">
                                <h5>{editingTheme ? 'Thema bearbeiten' : 'Neues Thema'}</h5>
                                <form onSubmit={handleThemeSubmit}>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Name *</label>
                                            <input type="text" className="form-control" value={themeData.name}
                                                onChange={e => setThemeData({ ...themeData, name: e.target.value })}
                                                placeholder="z.B. Minecraft-Abenteuer" required />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Bild-URL</label>
                                            <input type="text" className="form-control" value={themeData.image}
                                                onChange={e => setThemeData({ ...themeData, image: e.target.value })}
                                                placeholder="https://..." />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Min. Alter</label>
                                            <input type="number" className="form-control" value={themeData.min_age}
                                                onChange={e => setThemeData({ ...themeData, min_age: parseInt(e.target.value) })} min="4" max="18" />
                                        </div>
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Max. Alter</label>
                                            <input type="number" className="form-control" value={themeData.max_age}
                                                onChange={e => setThemeData({ ...themeData, max_age: parseInt(e.target.value) })} min="4" max="18" />
                                        </div>
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Sortierung</label>
                                            <input type="number" className="form-control" value={themeData.sort_order}
                                                onChange={e => setThemeData({ ...themeData, sort_order: parseInt(e.target.value) })} />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Beschreibung</label>
                                        <textarea className="form-control" rows="2" value={themeData.description}
                                            onChange={e => setThemeData({ ...themeData, description: e.target.value })}
                                            placeholder="Was erwartet die Kinder?" />
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button type="submit" className="btn btn-success">{editingTheme ? 'Speichern' : 'Erstellen'}</button>
                                        <button type="button" className="btn btn-secondary" onClick={resetThemeForm}>Abbrechen</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    <div className="row">
                        {themes.map(theme => (
                            <div key={theme.id} className="col-md-4 mb-3">
                                <div className={`card h-100 ${!theme.active ? 'opacity-50' : ''}`}>
                                    {theme.image && (
                                        <img src={theme.image} className="card-img-top" alt={theme.name} style={{ height: '120px', objectFit: 'cover' }} />
                                    )}
                                    <div className="card-body">
                                        <h5 className="card-title">{theme.name}</h5>
                                        <p className="card-text small text-muted">{theme.description || 'Keine Beschreibung'}</p>
                                        <span className="badge bg-info">{theme.min_age}-{theme.max_age} Jahre</span>
                                    </div>
                                    <div className="card-footer d-flex flex-wrap gap-1">
                                        <button className="btn btn-sm btn-outline-primary" onClick={() => handleEditTheme(theme)}>Bearbeiten</button>
                                        <button className={`btn btn-sm ${theme.active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                            onClick={() => toggleThemeActive(theme)}>
                                            {theme.active ? 'Deaktivieren' : 'Aktivieren'}
                                        </button>
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteTheme(theme.id)}>Loeschen</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* PACKAGES TAB */}
            {activeTab === 'packages' && (
                <>
                    <button className="btn btn-primary mb-3" onClick={() => setShowPackageForm(true)}>
                        + Neues Paket
                    </button>

                    {showPackageForm && (
                        <div className="card mb-4">
                            <div className="card-body">
                                <h5>{editingPackage ? 'Paket bearbeiten' : 'Neues Paket'}</h5>
                                <form onSubmit={handlePackageSubmit}>
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Name *</label>
                                            <input type="text" className="form-control" value={packageData.name}
                                                onChange={e => setPackageData({ ...packageData, name: e.target.value })}
                                                placeholder="z.B. Basis-Paket" required />
                                        </div>
                                        <div className="col-md-2 mb-3">
                                            <label className="form-label">Grundpreis *</label>
                                            <input type="number" className="form-control" value={packageData.price}
                                                onChange={e => setPackageData({ ...packageData, price: parseFloat(e.target.value) })}
                                                min="0" step="0.01" required />
                                        </div>
                                        <div className="col-md-2 mb-3">
                                            <label className="form-label">Pro Kind</label>
                                            <input type="number" className="form-control" value={packageData.price_per_child}
                                                onChange={e => setPackageData({ ...packageData, price_per_child: parseFloat(e.target.value) || 0 })}
                                                min="0" step="0.01" />
                                        </div>
                                        <div className="col-md-2 mb-3">
                                            <label className="form-label">Sortierung</label>
                                            <input type="number" className="form-control" value={packageData.sort_order}
                                                onChange={e => setPackageData({ ...packageData, sort_order: parseInt(e.target.value) })} />
                                        </div>
                                    </div>
                                    {parseFloat(packageData.price_per_child) > 0 && (
                                        <div className="alert alert-info mb-3">
                                            Beispiel bei 8 Kindern: {formatPrice(parseFloat(packageData.price) + (parseFloat(packageData.price_per_child) * 8))}
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label className="form-label">Kurzbeschreibung</label>
                                        <input type="text" className="form-control" value={packageData.description}
                                            onChange={e => setPackageData({ ...packageData, description: e.target.value })}
                                            placeholder="z.B. Der perfekte Start fuer eine unvergessliche Party" />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Enthaltene Leistungen</label>
                                        <textarea className="form-control" rows="3" value={packageData.includes}
                                            onChange={e => setPackageData({ ...packageData, includes: e.target.value })}
                                            placeholder="3 Stunden Raumnutzung, Betreuung, ..." />
                                    </div>
                                    <div className="form-check mb-3">
                                        <input type="checkbox" className="form-check-input" id="recommendedCheck"
                                            checked={packageData.recommended}
                                            onChange={e => setPackageData({ ...packageData, recommended: e.target.checked })} />
                                        <label className="form-check-label" htmlFor="recommendedCheck">Empfohlen (wird hervorgehoben)</label>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button type="submit" className="btn btn-success">{editingPackage ? 'Speichern' : 'Erstellen'}</button>
                                        <button type="button" className="btn btn-secondary" onClick={resetPackageForm}>Abbrechen</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    <div className="row">
                        {packages.map(pkg => (
                            <div key={pkg.id} className="col-md-6 mb-3">
                                <div className={`card h-100 ${pkg.recommended ? 'border-success border-2' : ''} ${!pkg.active ? 'opacity-50' : ''}`}>
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <h5 className="card-title">{pkg.name}</h5>
                                            <div className="text-end">
                                                <h5 className="text-success mb-0">{formatPrice(pkg.price)}</h5>
                                                {parseFloat(pkg.price_per_child) > 0 && (
                                                    <small className="text-muted">+ {formatPrice(pkg.price_per_child)}/Kind</small>
                                                )}
                                            </div>
                                        </div>
                                        {pkg.recommended && <span className="badge bg-success mb-2">Empfohlen</span>}
                                        <p className="card-text">{pkg.description}</p>
                                        {pkg.includes && (
                                            <p className="card-text small text-muted">
                                                <strong>Inklusive:</strong> {pkg.includes}
                                            </p>
                                        )}
                                    </div>
                                    <div className="card-footer d-flex gap-2">
                                        <button className="btn btn-sm btn-outline-primary" onClick={() => handleEditPackage(pkg)}>Bearbeiten</button>
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeletePackage(pkg.id)}>Loeschen</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* BLOCKED DATES TAB */}
            {activeTab === 'blocked' && (
                <>
                    <button className="btn btn-primary mb-3" onClick={() => setShowBlockForm(true)}>
                        + Datum sperren
                    </button>

                    {showBlockForm && (
                        <div className="card mb-4">
                            <div className="card-body">
                                <h5>Datum sperren</h5>
                                <form onSubmit={handleBlockSubmit}>
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Datum *</label>
                                            <input type="date" className="form-control" value={blockData.date}
                                                onChange={e => setBlockData({ ...blockData, date: e.target.value })} required />
                                        </div>
                                        <div className="col-md-8 mb-3">
                                            <label className="form-label">Grund</label>
                                            <input type="text" className="form-control" value={blockData.reason}
                                                onChange={e => setBlockData({ ...blockData, reason: e.target.value })}
                                                placeholder="z.B. Ferien, geschlossen" />
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button type="submit" className="btn btn-success">Sperren</button>
                                        <button type="button" className="btn btn-secondary" onClick={() => setShowBlockForm(false)}>Abbrechen</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {blockedDates.length === 0 ? (
                        <div className="alert alert-info">Keine gesperrten Termine.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr><th>Datum</th><th>Grund</th><th>Aktion</th></tr>
                                </thead>
                                <tbody>
                                    {blockedDates.map(bd => (
                                        <tr key={bd.date}>
                                            <td>{formatDate(bd.date)}</td>
                                            <td>{bd.reason || '-'}</td>
                                            <td>
                                                <button className="btn btn-sm btn-outline-success" onClick={() => handleUnblockDate(bd.date)}>
                                                    Entsperren
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {/* INVOICE MODAL */}
            {invoiceBooking && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Rechnung erstellen</h5>
                                <button type="button" className="btn-close" onClick={closeInvoiceModal}></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-2">
                                    <strong>{invoiceBooking.child_name}</strong> ({invoiceBooking.parent_name})<br />
                                    <small className="text-muted">
                                        {formatDate(invoiceBooking.date)} &middot; {invoiceBooking.package_name}
                                    </small>
                                </p>
                                <div className="mb-3">
                                    <label className="form-label">Anzahl teilgenommener Kinder</label>
                                    <input type="number" className="form-control" min="0" value={invoiceNumChildren}
                                        onChange={e => setInvoiceNumChildren(e.target.value)} />
                                    <small className="text-muted">
                                        Ursprünglich gebucht: {invoiceBooking.num_children} Kinder. Falls abweichend, hier anpassen.
                                    </small>
                                </div>
                                <div className="form-check mb-2">
                                    <input type="checkbox" className="form-check-input" id="invoiceApplyVat"
                                        checked={invoiceApplyVat} onChange={e => setInvoiceApplyVat(e.target.checked)} />
                                    <label className="form-check-label" htmlFor="invoiceApplyVat">zzgl. MwSt. ausweisen</label>
                                </div>
                                {invoiceApplyVat && (
                                    <div className="mb-3" style={{ maxWidth: '150px' }}>
                                        <label className="form-label">MwSt.-Satz (%)</label>
                                        <input type="number" className="form-control" min="0" step="0.01"
                                            value={invoiceVatRate} onChange={e => setInvoiceVatRate(e.target.value)} />
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeInvoiceModal} disabled={invoiceLoading}>
                                    Abbrechen
                                </button>
                                <button type="button" className="btn btn-success" onClick={handleCreateInvoice} disabled={invoiceLoading}>
                                    {invoiceLoading ? 'Wird erstellt...' : 'Rechnung erstellen'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default GeburtstagManagerV2;
